import React, { useCallback, useState } from 'react'
import { Box, Grid, Stack, TextField, Typography, IconButton, Tooltip, Chip } from '@mui/material';
import { useNavigate } from "react-router-dom";
// icons
import EastIcon from '@mui/icons-material/East';
// utils and helpers
import { numberOnly, debounce, formatTimestampToWithoutTime } from "utilities/common";
import { getClosureForms } from "../../../redux/slices/hyperwolf/inventoryDistribution";
// custom components
import CommonLayout from 'layouts/DashboardLayout'
import MDButton from 'components/MDButton';
import { DateFilter } from "common/filters";
import AgGridAccordionTable from "components/tables/AgGridAccordionTable";

const ClosureForms = () => {
  const navigate = useNavigate()

  const [inputFilters, setInputFilters] = useState({});
  const [customFilters, setCustomFilters] = useState({});
  const [refreshTable, setRefreshTable] = useState(false);
  const [resetDateFilter, setResetDateFilter] = useState(false);

  const columnDefs = [
    {
      headerName: 'Date',
      field: 'date',
      suppressMenu: false,
      minWidth: 400,
      flex: 1.5,
      sortable: false,
      className: 'field-region',
      valueGetter: ({ data }) => data?.date ? formatTimestampToWithoutTime(Number(data?.date)) : "-",
    },
    {
      headerName: 'Open Forms',
      field: 'openForm',
      flex: 1,
      suppressMenu: false,
      sortable: false,
      className: 'field-opening-hour',
      valueGetter: ({ data }) => data?.openForm || 0,
    },
    {
      headerName: 'Closed Forms',
      field: 'closedForm',
      flex: 1,
      suppressMenu: false,
      sortable: false,
      className: 'field-closing-hour',
      valueGetter: ({ data }) => data?.closedForm || 0,
    },
    {
      headerName: 'Pending Forms',
      field: 'pendingForms',
      flex: 1,
      suppressMenu: false,
      sortable: false,
      className: 'field-closing-hour',
      valueGetter: ({ data }) => data?.pendingForms || 0,
    },
    {
      headerName: 'Total revenue',
      field: 'totalRevenue',
      flex: 1,
      suppressMenu: false,
      sortable: false,
      className: 'field-closing-hour',
      valueGetter: ({ data }) => data?.totalRevenue?.toString() ? `$${data?.totalRevenue?.toLocaleString()}` : "-",
    },
  ];

  const expandedColumnDefs = [
    {
      headerName: 'Regions',
      field: 'regionName',
      suppressMenu: false,
      minWidth: 300,
      flex: 1.5,
      sortable: false
    },
    {
      headerName: 'Sub Regions',
      field: 'subRegions',
      flex: 1,
      minWidth: 150,
      suppressMenu: false,
      sortable: false,
      cellRenderer: ({ data }) => {
        if (data?.subRegions?.length === 1) {
          return data?.subRegions;
        }

        return <Tooltip
          arrow
          title={
            <>
              {data?.subRegions?.map(subRegion => (
                <Typography key={subRegion} variant="caption" component="p" fontWeight={500} textAlign="left">
                  {subRegion}
                </Typography>
              ))}
            </>
          }
        >
          <span className='table-row-chip chip-cus' style={{ cursor: "pointer" }}>
            {data?.subRegions?.[0]}
            <Chip
              key={data?._id || ""}
              label={`+${data?.subRegions?.length - 1}`}
              style={{ p: 0, m: 0 }}
            />
          </span>
        </Tooltip>
      }
    },
    {
      headerName: 'Driver Name',
      field: 'driversName',
      flex: 1,
      minWidth: 150,
      suppressMenu: false,
      sortable: false,
      cellRenderer: ({ data }) => {
        if (!data?.driversName?.length) return "-";
        if (data?.driversName?.length === 1) {
          return data?.driversName;
        }

        return <Tooltip
          arrow
          title={
            <>
              {data?.driversName?.map(driver => (
                <Typography key={driver} variant="caption" component="p" fontWeight={500} textAlign="left">
                  {driver}
                </Typography>
              ))}
            </>
          }
        >
          <span className='table-row-chip chip-cus' style={{ cursor: "pointer" }}>
            {data?.driversName?.[0]}
            <Chip
              key={data?._id || ""}
              label={`+${data?.driversName?.length - 1}`}
              style={{ p: 0 }}
            />
          </span>
        </Tooltip>
      }
    },
    {
      headerName: 'Open Forms',
      field: 'openForm',
      flex: 1,
      minWidth: 150,
      suppressMenu: false,
      sortable: false,
      className: 'field-opening-hour',
    },
    {
      headerName: 'Closed Forms',
      field: 'closedForm',
      flex: 1,
      minWidth: 140,
      suppressMenu: false,
      sortable: false,
      className: 'field-closing-hour',
    },
    {
      headerName: 'Pending Forms',
      field: 'pendingForms',
      flex: 1,
      minWidth: 140,
      suppressMenu: false,
      sortable: false,
      className: 'field-closing-hour',
    },
    {
      headerName: 'Total Revenue',
      field: 'totalRevenue',
      flex: 1,
      minWidth: 140,
      suppressMenu: false,
      sortable: false,
      className: 'field-closing-hour',
      valueGetter: ({ data }) => data?.totalRevenue ? `$${data?.totalRevenue?.toLocaleString()}` : "-",
    },
    {
      headerName: '',
      field: 'actions',
      cellStyle: { textAlign: 'right', justifyContent: 'end' },
      headerClass: 'right-aligned-cell-header',
      flex: 0.75,
      minWidth: 80,
      // pinned: 'right',
      className: 'field-actions',
      cellRenderer: ({ data, context }) => {
        const { distributionStatus } = context?.parentData || {};

        if (!data?.driversName?.length || distributionStatus !== "freeze") return;

        return <Box display="flex" flexDirection="row" justifyContent="end" gap={0.5}>
          <IconButton onClick={() => navigate(`/hyperwolf/closure-forms/region/${data.distributionId}?region=${data?.regionId}&date=${data?.date}`)} size='small'>
            <EastIcon width='16px' height='16px' />
          </IconButton>
        </Box>
      },
    },
  ];

  const handleDebounceChange = useCallback(debounce((e) => {
    const { name, value } = e.target

    setCustomFilters(prev => ({ ...prev, [name]: numberOnly(value) }));
    setRefreshTable(prev => !prev)
  }, 1000), []);

  const handleDateChange = async (values) => {
    setCustomFilters(prev => ({ ...prev, ...values }));
    setRefreshTable(prev => !prev)
  }

  const handleClearFilters = () => {
    setInputFilters({ search: customFilters.search || "" });
    setCustomFilters({ search: customFilters.search || "" });
    setRefreshTable(prev => !prev)
    setResetDateFilter(prev => !prev)
  }

  return (
    <CommonLayout>
      <Box p={{ xs: 2, sm: 3, lg: 4 }}>
        <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={3}>
          <Grid item xs={12} sm={4}>
            <Typography variant='h4' fontWeight="semibold">Closure Overview</Typography>
          </Grid>
        </Grid>

        {/* Filters */}
        <Box className='cus-filter-sec' mb={2}>
          <Grid className='filter-box' container alignItems='center' justifyContent='flex-start' spacing={1.5}>
            <Grid item xs={12} sm={4} md={4} xxl={4}>
              <Box className='filter-price'>
                <Stack direction='row' alignItems='center' gap={1} className="revenue-filters">
                  <Typography variant='body2' color='secondary' whiteSpace='nowrap'>Revenue ($)</Typography>
                  <TextField
                    size='small'
                    name='minRevenue'
                    label='Min'
                    fullWidth
                    value={inputFilters.minRevenue || ""}
                    onChange={e => {
                      const value = e.target.value;
                      const numValue = numberOnly(value);
                      if ((!numValue && value) || numValue >= 99999) return;

                      setInputFilters(prev => ({ ...prev, minRevenue: numValue }))
                      handleDebounceChange(e)
                    }}
                  />
                  <TextField
                    size='small'
                    name='maxRevenue'
                    label='Max'
                    fullWidth
                    value={inputFilters.maxRevenue || ""}
                    onChange={e => {
                      const value = e.target.value;
                      const numValue = numberOnly(value);
                      if ((!numValue && value) || numValue >= 99999) return;

                      setInputFilters(prev => ({ ...prev, maxRevenue: numValue }))
                      handleDebounceChange(e)
                    }}
                  />
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12} lg={3} xxxl={2}>
              <DateFilter
                handleChange={handleDateChange}
                resetDateFilter={resetDateFilter}
              />
            </Grid>
            <Grid item xs={12} lg={3} xxxl={2}>
              <MDButton
                sx={{ minWidth: "120px", fontWeight: "normal" }}
                type="button"
                variant="outlined"
                color="dark"
                disabled={!Object.values(customFilters || {})?.filter(Boolean)?.length}
                onClick={handleClearFilters}
              >
                Clear All
              </MDButton>
            </Grid>
          </Grid>
        </Box>

        {/* Products listing */}
        <div className="cus-four-columns">
          <AgGridAccordionTable
            tableName="Kit verification"
            columnDefs={columnDefs}
            expandedColumnDefs={expandedColumnDefs}
            getDataMethod={getClosureForms}
            customFilters={customFilters}
            refreshTable={refreshTable}
            onChildCellClicked={(props) => {
              const data = props?.data || {}
              const { distributionStatus } = props?.context?.parentData || {};

              if (!data?.driversName?.length || distributionStatus !== "freeze") return;

              // Prevent row click if click originated from the actions column
              if (props?.column && (["actions"].includes(props.column.getColId()))) {
                return;
              }
              navigate(`/hyperwolf/closure-forms/region/${data.distributionId}?region=${data?.regionId}&date=${data?.date}`)
            }}
          />
        </div>
      </Box>
    </CommonLayout>
  )
}

export default ClosureForms
